/**
 * Shared chat view used by both Tenant (`/app/chat`) and Portal (`/portal/chat`).
 *
 * Left panel: thread list (one per case).
 * Right panel: message list + composer + real-time WebSocket subscription.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, MessageSquare, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/time";
import { useChatSocket } from "@/lib/useChatSocket";
import NewChatModal from "@/components/NewChatModal";
import Linkified from "@/components/Linkified";

export default function ChatView({ origin = "tenant" }) {
    const { user } = useAuth();
    const [params, setParams] = useSearchParams();
    const [threads, setThreads] = useState([]);
    const [activeId, setActiveId] = useState(params.get("thread") || null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [typing, setTyping] = useState({});
    const [readBy, setReadBy] = useState({});
    const listEndRef = useRef(null);

    const loadThreads = useCallback(async () => {
        try {
            const { data } = await api.get("/chat/threads");
            setThreads(data);
            if (!activeId && data.length) {
                setActiveId(data[0].id);
            }
        } catch (e) { toast.error(apiError(e)); }
    }, [activeId]);

    useEffect(() => { loadThreads(); }, [loadThreads]);

    const loadMessages = useCallback(async (tid) => {
        if (!tid) { setMessages([]); return; }
        setLoading(true);
        try {
            const { data } = await api.get(`/chat/threads/${tid}/messages?limit=100`);
            setMessages(data);
            // Mark as read
            await api.post(`/chat/threads/${tid}/read`).catch(() => {});
            loadThreads();
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [loadThreads]);

    useEffect(() => { loadMessages(activeId); }, [activeId, loadMessages]);

    const selectThread = useCallback((tid) => {
        setActiveId(tid);
        setParams({ thread: tid }, { replace: true });
    }, [setParams]);

    // WebSocket subscription for the active thread
    const onSocketMessage = useCallback((evt) => {
        if (evt.type === "message" && evt.message) {
            setMessages(prev => prev.find(m => m.id === evt.message.id) ? prev : [...prev, evt.message]);
            api.post(`/chat/threads/${activeId}/read`).catch(() => {});
            loadThreads();
        }
        if (evt.type === "typing" && evt.user_id !== user.id) {
            setTyping(prev => ({ ...prev, [evt.user_id]: { name: evt.user_name, ts: Date.now() } }));
        }
        if (evt.type === "read") {
            setReadBy(prev => ({ ...prev, [evt.user_id]: evt.at }));
        }
    }, [activeId, loadThreads, user.id]);

    const { status: socketStatus, send: sendWS } = useChatSocket(activeId, { onMessage: onSocketMessage });
    const lastTypingSentRef = useRef(0);
    const onComposerChange = (val) => {
        setDraft(val);
        const now = Date.now();
        if (sendWS && now - lastTypingSentRef.current > 2000) {
            sendWS({ type: "typing", user_id: user.id, user_name: user.name || user.email });
            lastTypingSentRef.current = now;
        }
    };

    // Scroll to bottom when messages change
    useEffect(() => {
        listEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages.length]);

    const send = async () => {
        const body = draft.trim();
        if (!body || !activeId) return;
        setSending(true);
        try {
            const { data: msg } = await api.post(`/chat/threads/${activeId}/messages`, { body });
            // Optimistically append (server also broadcasts but excludes sender)
            setMessages(prev => prev.find(m => m.id === msg.id) ? prev : [...prev, msg]);
            setDraft("");
            loadThreads();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSending(false); }
    };

    const activeThread = useMemo(() => threads.find(t => t.id === activeId), [threads, activeId]);

    return (
        <div className="h-[calc(100vh-7rem)] flex flex-col" data-testid="chat-page">
            <div className="mb-4">
                <div className="overline">Chat</div>
                <h1 className="display-display mt-1">{origin === "portal" ? "Chat with your lawyer" : "Client chat"}</h1>
                <p className="text-sm text-muted-foreground mt-2">
                    {origin === "portal"
                        ? "Send a message to your legal team. Replies arrive here in real-time."
                        : "Real-time conversations with clients, organised per case. Reassignments carry the chat history automatically."}
                </p>
            </div>

            <div className="flex-1 grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4 min-h-0">
                {/* Thread list */}
                <aside className="bg-card border border-border flex flex-col min-h-0" data-testid="chat-thread-list">
                    <div className="p-3 border-b border-border flex items-center justify-between gap-2">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                            {threads.length} thread{threads.length === 1 ? "" : "s"}
                        </span>
                        {origin !== "portal" && (
                            <button
                                onClick={() => setShowNew(true)}
                                data-testid="chat-new-btn"
                                className="text-[10px] uppercase tracking-[0.15em] text-primary hover:text-[#E0BB48] inline-flex items-center gap-1"
                            >
                                <Plus className="w-3 h-3"/> New
                            </button>
                        )}
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {threads.length === 0 && (
                            <div className="p-4 text-xs text-muted-foreground" data-testid="chat-empty-threads">
                                {origin === "portal"
                                    ? "No active conversations yet. Your lawyer will open one once your case is created."
                                    : "No threads yet. Click ‘+ New’ above to start one with a client or as a group."}
                            </div>
                        )}
                        {threads.map(t => (
                            <button
                                key={t.id}
                                onClick={() => selectThread(t.id)}
                                data-testid={`chat-thread-item-${t.id}`}
                                data-thread-testid={`chat-thread-${t.id}`}
                                className={`w-full text-left px-3 py-3 border-b border-border hover:bg-background transition-colors ${activeId === t.id ? "bg-background border-l-2 border-l-primary" : ""}`}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <div className="text-sm text-foreground truncate">{t.title}</div>
                                    {t.unread > 0 && (
                                        <span className="bg-primary text-primary-foreground text-[10px] font-medium px-1.5 py-0.5 rounded-full" data-testid={`chat-unread-${t.id}`}>
                                            {t.unread}
                                        </span>
                                    )}
                                </div>
                                <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                                    {t.last_preview || "No messages yet"}
                                </div>
                            </button>
                        ))}
                    </div>
                </aside>

                {/* Conversation pane */}
                <section className="bg-card border border-border flex flex-col min-h-0" data-testid="chat-conversation">
                    {!activeId && (
                        <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                            <div className="text-center">
                                <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" strokeWidth={1.2}/>
                                Select a conversation
                            </div>
                        </div>
                    )}
                    {activeId && (
                        <>
                            <header className="px-4 py-3 border-b border-border flex items-center justify-between">
                                <div>
                                    <div className="text-sm text-foreground" data-testid="chat-active-title">{activeThread?.title || ""}</div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-0.5">Case discussion</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`w-2 h-2 rounded-full ${socketStatus === "open" ? "bg-emerald-500" : "bg-amber-500"}`} aria-label={socketStatus === "open" ? "Live" : "Connecting"} data-testid="chat-socket-status">
                                        <span className="sr-only">{socketStatus === "open" ? "Live" : "Connecting"}</span>
                                    </span>
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                        {socketStatus === "open" ? "Live" : "Connecting…"}
                                    </span>
                                </div>
                            </header>

                            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
                                {loading && <div className="text-center text-muted-foreground text-xs"><Loader2 className="w-4 h-4 animate-spin inline mr-2"/>Loading…</div>}
                                {!loading && messages.length === 0 && (
                                    <div className="text-center text-muted-foreground text-xs py-12">
                                        No messages yet. Say hello!
                                    </div>
                                )}
                                {messages.map((m, idx) => {
                                    const isLastSelf = m.sender_id === user.id && idx === messages.length - 1;
                                    const readers = isLastSelf ? Object.entries(readBy).filter(([uid]) => uid !== user.id) : [];
                                    return <MessageBubble key={m.id} m={m} self={m.sender_id === user.id} readReceipt={readers.length > 0 ? readers.length : null}/>;
                                })}
                                {Object.keys(typing).length > 0 && (
                                    <div className="text-[11px] text-muted-foreground italic flex items-center gap-2" data-testid="chat-typing-indicator">
                                        <span className="inline-flex gap-0.5">
                                            <span className="w-1 h-1 bg-muted rounded-full animate-pulse"></span>
                                            <span className="w-1 h-1 bg-muted rounded-full animate-pulse" style={{ animationDelay: "0.15s" }}></span>
                                            <span className="w-1 h-1 bg-muted rounded-full animate-pulse" style={{ animationDelay: "0.3s" }}></span>
                                        </span>
                                        {Object.values(typing).map(t => t.name).filter(Boolean).join(", ") || "Someone"} typing…
                                    </div>
                                )}
                                <div ref={listEndRef}/>
                            </div>

                            <footer className="border-t border-border p-3 flex gap-2">
                                <textarea
                                    value={draft}
                                    onChange={e => onComposerChange(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                            e.preventDefault();
                                            send();
                                        }
                                    }}
                                    placeholder="Type a message… (Enter to send, Shift+Enter for newline)"
                                    rows={1}
                                    className="flex-1 bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm resize-none max-h-32"
                                    data-testid="chat-composer-input"
                                />
                                <button
                                    onClick={send}
                                    disabled={sending || !draft.trim()}
                                    data-testid="chat-send-btn"
                                    className="px-4 py-2 bg-primary text-primary-foreground hover:bg-[#C9A537] disabled:opacity-40 text-xs font-medium uppercase tracking-[0.15em] inline-flex items-center gap-2"
                                >
                                    {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>}
                                    Send
                                </button>
                            </footer>
                        </>
                    )}
                </section>
            </div>
            <NewChatModal
                open={showNew}
                onClose={() => setShowNew(false)}
                onOpenThread={(tid) => {
                    setShowNew(false);
                    loadThreads();
                    selectThread(tid);
                }}
            />
        </div>
    );
}

function MessageBubble({ m, self, readReceipt }) {
    if (m.is_system) {
        return (
            <div className="text-center text-[11px] text-muted-foreground italic py-2" data-testid="chat-system-msg">
                <Linkified text={m.body} />
            </div>
        );
    }
    const time = m.created_at ? fmtDateTime(m.created_at) : "";
    return (
        <div className={`flex ${self ? "justify-end" : "justify-start"}`} data-testid="chat-message">
            <div className={`max-w-[75%] ${self ? "bg-primary/15 border border-primary/40" : "bg-background border border-border"} px-3 py-2`}>
                {!self && (
                    <div className="text-[10px] uppercase tracking-[0.15em] text-primary mb-1">
                        {m.sender_name}
                        {m.sender_role && m.sender_role !== "client_portal" && (
                            <span className="text-muted-foreground normal-case ml-1.5 tracking-normal">· {m.sender_role.replace(/_/g, " ")}</span>
                        )}
                    </div>
                )}
                <Linkified text={m.body} as="div" className="text-sm whitespace-pre-wrap text-foreground" />
                <div className="text-[10px] text-muted-foreground mt-1 text-right flex items-center justify-end gap-1.5">
                    {time}
                    {self && readReceipt && (
                        <span className="text-emerald-400" data-testid="chat-read-receipt" title={`Read by ${readReceipt} participant(s)`}>✓✓</span>
                    )}
                </div>
            </div>
        </div>
    );
}
